import { action, observable, makeAutoObservable } from 'mobx';
import api from 'src/api';
import { default as showError } from 'src/utils/error.jsx';
import type { EditableTableRowProps } from './EditableTableRow';

export class EditableTableRowState {
    public readonly props: EditableTableRowProps;
    @observable key: string;
    @observable saving: boolean = false;
    @observable value: string;

    public constructor(props: EditableTableRowProps) {
        this.props = props;
        this.key = props.translationKey;
        this.value = props.translationValue;
        makeAutoObservable(this);
    }

    @action onKeyChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (this.saving) {
            event.preventDefault();
        }

        this.key = event.target.value;
    };

    @action onValueChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (this.saving) {
            event.preventDefault();
        }

        this.value = event.target.value;
    };

    private onSaveClickProcess = async (): Promise<void> => {
        this.saving = true;

        try {
            const newUrl = `/translations/${this.props.groupName}/${this.props.language}/${this.key}`;
            const oldUrl = `/translations/${this.props.groupName}/${this.props.language}/${this.props.translationKey}`;

            if (this.keyChanged()) {
                await api.del(oldUrl);
            }

            await api.post(newUrl, { value: this.value });

            await this.props.onUpdate();
            this.saving = false;
        } catch (error) {
            console.error(error);
            showError('Unable to save translation property');
            this.saving = false;
        }
    };

    @action onSaveClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
        if (this.keyOrValueChanged()) {
            if (this.key.trim() === '') {
                showError('Translation key must not be empty');
            } else if (this.props.uniqueKeys.includes(this.key)) {
                showError(`Conflicting language translations for key: "${this.key}"`);
            } else {
                this.onSaveClickProcess().catch((error) => {
                    console.error(error);
                });
            }
        } else {
            this.props.onCancel(event);
        }
    };

    private onDeleteClickProcess = async (): Promise<void> => {
        this.saving = true;

        try {
            const url = `/translations/${this.props.groupName}/${this.props.language}/${this.props.translationKey}`;
            await api.del(url);
            await this.props.onUpdate();
            this.saving = false;
        } catch (err) {
            console.error(err);
            showError('Unable to delete translation property');
            this.saving = false;
        }
    };

    @action onDeleteClick = (): void => {
        // eslint-disable-next-line no-alert
        if (confirm(`Are you sure to delete "${this.props.translationKey}" from "${this.props.language}"?`)) {
            this.onDeleteClickProcess().catch((err) => {
                console.error(err);
            });
        }
    };

    @action keyOrValueChanged = (): boolean => this.keyChanged() || this.valueChanged();

    @action keyChanged = (): boolean => this.key !== this.props.translationKey;

    @action valueChanged = (): boolean => this.value !== this.props.translationValue;
}
